import React, { useEffect, useState } from "react";
import { useGame } from "../api/copenhunt/v1/copenhunt_rbt_react";
import GameMap from "../components/Map";
import "../styles/AdminGamePage.css";
import { getPlayersByTeam } from "../utils";
import { useGameData } from "../hooks/useGameData";
import GameOverview from "../components/GameOverview";
import AdminControlPanel from "../components/AdminControlPanel";
import TeamsDisplay from "../components/TeamsDisplay";
import LocationsDisplay from "../components/LocationsDisplay";
import { GameCondition } from "../api/copenhunt/v1/copenhunt_pb";

const AdminGamePage: React.FC = () => {
  const {
    gameId,
    overviewResponse,
    playerInfos,
    locations,
    teamIds,
    teamColors,
    objectiveShapes,
    teamNumbers,
    events,
    currentTimestamp,
    latestPingTimestamp,
  } = useGameData();

  const {
    mutators: {
      start,
      pause,
      unpause,
      deletePlayer,
      removeObjective,
      deleteTeam,
      deleteLocation,
    },
  } = useGame({ id: gameId ?? "" });

  const [currentTime, setCurrentTime] = useState(Date.now() / 1000);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now() / 1000);
    }, 5 * 1000);
    return () => clearInterval(interval);
  }, []);

  const playersByTeam = getPlayersByTeam(playerInfos, teamIds);

  const handleDelete = async (playerId: string, teamId: string) => {
    const { aborted } = await deletePlayer({ playerId, teamId });
    if (aborted) {
      console.log("Couldn't delete player");
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    const { aborted } = await deleteTeam({ teamId });
    if (aborted) {
      console.log("Couldn't delete team");
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    const { aborted } = await deleteLocation({ locationId });
    if (aborted) {
      console.log("Couldn't delete location");
    }
  };

  const handleRemoveObjective = async (
    teamId: string,
    objectiveId: string,
    canReclaim: boolean
  ) => {
    const { aborted } = await removeObjective({
      teamId,
      objectiveId,
      canReclaim,
    });
    if (aborted) {
      console.log("Couldn't remove objective");
    }
  };

  if (!gameId || !overviewResponse) {
    return <div>Loading...</div>;
  }
  const { locationDelaySeconds } = overviewResponse;

  const hunterTeamInfo = Object.values(overviewResponse.teamInfoById).find(
    (team) => team.isHunter
  );

  return (
    <div className="admin-container">
      <div className="row centered-link">
        <p>
          Link to join the game:&nbsp;
          <a
            href={`${window.location.protocol}//${window.location.host}/game/${gameId}/register`}
          >
            {`${window.location.protocol}//${window.location.host}/game/${gameId}/register`}
          </a>
        </p>
      </div>

      <div className="row flex-wrap">
        <div className="col col-80">
          <GameMap
            forPlayerId={null}
            forTeamId={null}
            playerInfos={playerInfos}
            teamColors={teamColors}
            teamNumbers={teamNumbers}
            objectiveShapes={objectiveShapes}
            locations={locations}
            teamInfoById={overviewResponse.teamInfoById}
            locationDelaySeconds={locationDelaySeconds}
            latestPingTimestamp={latestPingTimestamp}
            pingIntervalSeconds={overviewResponse.pingIntervalSeconds}
            mode={overviewResponse.mode}
          />
        </div>
        <div className="col col-20">
          <GameOverview
            condition={overviewResponse.condition}
            secondsToNextSwitch={overviewResponse.secondsToNextSwitch}
            isPaused={overviewResponse.isPaused}
            totalSeconds={overviewResponse.roundDurationSeconds}
            secondsToNextPing={overviewResponse.secondsToNextPing}
            pingIntervalSeconds={overviewResponse.pingIntervalSeconds}
            hunterPingsRemaining={hunterTeamInfo?.extraPingsRemaining}
            events={events}
            currentTimestamp={currentTimestamp ?? currentTime}
            latestPingTimestamp={latestPingTimestamp}
            isAdmin
          />
        </div>
      </div>
      <div className="row control-panel-row">
        <AdminControlPanel
          gameId={gameId}
          condition={overviewResponse.condition}
          teamInfoById={overviewResponse.teamInfoById}
          onStart={start}
          onPause={pause}
          onUnpause={unpause}
          isPaused={overviewResponse.isPaused}
          mode={overviewResponse.mode}
          pingIntervalSeconds={overviewResponse.pingIntervalSeconds}
        />
      </div>

      <TeamsDisplay
        playersByTeam={playersByTeam}
        teamColors={teamColors}
        teamNumbers={teamNumbers}
        teamInfoById={overviewResponse.teamInfoById}
        teamIds={teamIds}
        objectiveShapes={objectiveShapes}
        onDeletePlayer={handleDelete}
        onRemoveObjective={handleRemoveObjective}
        currentTime={currentTime}
        onDeleteTeam={handleDeleteTeam}
        gameCondition={overviewResponse.condition}
      />
      {overviewResponse.condition === GameCondition.NOT_STARTED ? (
        <LocationsDisplay
          locations={locations}
          objectiveShapes={objectiveShapes}
          onDeleteLocation={handleDeleteLocation}
        />
      ) : null}
    </div>
  );
};

export default AdminGamePage;
